import { contactExportState } from "./const/contactExportState.js";
import { backendEventsFactory } from "./backendEventsFactory.js";
import { BotStoppedError } from "./common/errors/BotStoppedError.js";
import { backendCommandsFactory } from "./appImportBroker.js";
import { utils } from "./common/utils.js";
import { tagMetadataKeys } from "./export/tagMetadataKeys.js";
import { errorParametersFactory } from "./common/errors/errorParametersFactory.js";
import { logEntryIds } from "./common/logging/logEntryIds.js";
import { Mutex } from "./common/Mutex.js";
import { BadGatewayError } from "./common/errors/BadGatewayError.js";
import { delay } from "./common/delay.js";
import { ExceedsLimitLinkedInError } from "./common/errors/ExceedsLimitLinkedInError.js";
import { EmptySearchPageError } from "./common/errors/EmptySearchPageError.js";

export class ExportContactsBot {
  #broker;

  #state = {
    phase: contactExportState.notStarted,
    exported: 0,
    processed: 0,
    expected: null,
    navigationUrl: null,
  };
  #ids = [];
  #emptyPagesCount = 0;
  #contactsIterator;
  #mutex = Mutex();

  constructor(broker, contactsIterator, type) {
    this.#broker = broker;
    this.#contactsIterator = contactsIterator;
    this.#state.type = type;
  }

  async count(metadata) {
    this.#state.metadata = metadata;
    try {
      await this.#contactsIterator.getNext(this.#state.metadata);
    } catch (error) {
      if (error instanceof EmptySearchPageError) {
        return 0;
      }

      throw error;
    }

    const result = this.#contactsIterator.getTotal();
    return result;
  }

  async start(metadata) {
    try {
      this.#state.metadata = metadata;
      this.#state.phase = contactExportState.starting;

      const exportStarting = backendEventsFactory.createExportStarting(
        this.#state,
        this.#state.metadata
      );
      await this.#fireEvent(exportStarting);

      this.#state.phase = contactExportState.running;
      const exportStarted = backendEventsFactory.createExportStarted(
        this.#state,
        this.#state.metadata
      );
      await this.#fireEvent(exportStarted);

      // noinspection ES6MissingAwait
      this.#exportContactsAsync();
    } catch (error) {
      await this.#internalStopAsync(error);
    }
  }

  async stop(metadata) {
    //ToDo: double check
    //if (_state.phase === contactExportState.running || _state.phase === contactExportState.starting) {
    if (this.#state.phase === contactExportState.running) {
      await this.#internalStopAsync();
    } else {
      throw new BotStoppedError("Bot is already stopped", metadata);
    }
    const result = this.#state;
    return result;
  }

  getState() {
    const result = this.#state;
    return result;
  }

  async #getCurrentDateTime(metadata) {
    const command =
      backendCommandsFactory.apiProxy.createGetCurrentDateTime(metadata);
    const result = await this.#broker.publish(command);
    return result;
  }

  async #createImportAsync() {
    const exportMetadata = this.#contactsIterator.getMetadata();
    const currentDateTime = await this.#getCurrentDateTime(exportMetadata);
    const exportName = utils.dateTimeToString(currentDateTime);
    const exportType = this.#contactsIterator.getType();

    const createExportTagCommand =
      backendCommandsFactory.apiProxy.createCreateExportTag(
        exportName,
        exportType,
        exportMetadata,
        this.#state.metadata
      );
    const exportTag = await this.#broker.publish(createExportTagCommand);
    this.#state.exportId = exportTag.id;
  }

  async #exportContactsAsync() {
    let error = null;
    try {
      const getNextWithClassScope = this.#contactsIterator.getNext.bind(
        this.#contactsIterator
      );

      while (this.#contactsIterator.canMove()) {
        const response = await this.#concurrentRun(
          getNextWithClassScope,
          [this.#state.metadata],
          false
        );

        this.#state.expected = this.#contactsIterator.getTotal();
        //nothing to iterate, it might happens if last contact has invalid public identifier
        if (response.found.length === 0) {
          this.#emptyPagesCount++;
          if (this.#emptyPagesCount >= 5) {
            break;
          }
        }

        this.#emptyPagesCount = 0;
        const foundContacts = response.found;
        if (!this.#state.exportId) {
          await this.#createImportAsync();
          const getExportHrefCommand =
            backendCommandsFactory.automation.createGetExportHref(
              this.#state.exportId,
              this.#state.metadata
            );
          this.#state.navigationUrl =
            await this.#broker.publish(getExportHrefCommand);
        }

        if (foundContacts.length > 0) {
          let notFinished = true;
          let attempt = 0;
          //reliability
          while (notFinished) {
            try {
              const exportTaggedContactsCommand =
                backendCommandsFactory.apiProxy.createExportTaggedContacts(
                  this.#state.exportId,
                  foundContacts,
                  this.#state.metadata
                );
              await this.#concurrentRun(
                this.#broker.publish,
                [exportTaggedContactsCommand],
                false
              );

              let newContactsCount = 0;
              for (const contact of foundContacts) {
                if (this.tryToAdd(contact)) {
                  newContactsCount++;
                }
              }

              this.#state.exported += newContactsCount;
              this.#state.processed += response.processed;

              notFinished = false;
            } catch (exception) {
              attempt++;
              // be sure that BadGatewayError will not be infinitive
              if (!(exception instanceof BadGatewayError) || attempt > 30) {
                throw exception;
              }

              await delay.waitAsync(5);
            }
          }
        }

        const exportChanged = backendEventsFactory.createExportChanged(
          this.#state,
          this.#state.metadata
        );
        await this.#fireEvent(exportChanged);

        //ToDo: questionable = copied it as it is from iterators
        await delay.randomWaitAsync(2, 4);
      }
    } catch (e) {
      if (e instanceof ExceedsLimitLinkedInError) {
        await this.#reportTooManyRequests();
      }

      if (
        !(
          e instanceof BotStoppedError || e instanceof ExceedsLimitLinkedInError
        )
      ) {
        error = e;
      }
    } finally {
      await this.#internalStopAsync(error);
    }
  }

  tryToAdd(contact) {
    let key;
    if (contact.miniProfile.entityId) {
      key = contact.miniProfile.entityId;
    } else if (contact.miniProfile.publicIdentifier) {
      key = contact.miniProfile.publicIdentifier;
    } else {
      key = contact.miniProfile.salesNavigatorEntityId;
    }

    if (!this.#ids.includes(key)) {
      this.#ids.push(key);
      return true;
    }

    return false;
  }

  async #internalStopAsync(error) {
    if (
      this.#state.phase !== contactExportState.failed &&
      this.#state.phase !== contactExportState.completed &&
      this.#state.phase !== contactExportState.completing
    ) {
      try {
        this.#state.phase = contactExportState.completing;
        const exportCompleting = backendEventsFactory.createExportCompleting(
          this.#state,
          this.#state.metadata
        );
        await this.#fireEvent(exportCompleting);

        if (this.#state.exportId) {
          try {
            const metadata = {
              key: tagMetadataKeys.ExportStoppedKey,
              value: tagMetadataKeys.TrueValue,
            };

            const stopExportCommand =
              backendCommandsFactory.apiProxy.createAddMetadataToTag(
                this.#state.exportId,
                metadata,
                this.#state.metadata
              );
            const reliableCommand =
              backendCommandsFactory.reliability.createReliablePublish(
                stopExportCommand,
                this.#state.metadata
              );
            await this.#concurrentRun(
              this.#broker.publish,
              [reliableCommand],
              true
            );
          } catch (stopError) {
            console.error(stopError);
          }
        }

        if (!error) {
          this.#state.phase = contactExportState.completed;
          const exportCompleted = backendEventsFactory.createExportCompleted(
            this.#state,
            this.#state.metadata
          );
          await this.#fireEvent(exportCompleted);
        } else {
          await this.#onErrorAsync(error);
        }
      } catch (exp) {
        if (!(exp instanceof BotStoppedError)) {
          await this.#onErrorAsync(exp);
        }
      }
    }
  }

  async #onErrorAsync(e) {
    await this.#logError(e);
    this.#state.phase = contactExportState.failed;
    this.#state.exportError = {
      message: e.message,
      name: e.name,
      stack: e.stack,
    };

    const exportFailed = backendEventsFactory.createExportFailed(
      this.#state,
      this.#state.metadata
    );
    await this.#fireEvent(exportFailed);
  }

  async #concurrentRun(functionToRun, args, isStop) {
    //#131 we always await previous operation. If it is stop then we should skip errors causes by previous command
    await this.#mutex.lock();
    try {
      // try {
      //     await this.#onGoingCommand;
      // } catch (exp) {
      //     if (!isStop) {
      //         throw exp;
      //     }
      // }
      if (this.#state.phase === contactExportState.running || isStop) {
        const result = await functionToRun(...args);
        return result;
      } else {
        throw new BotStoppedError(
          "Bot is already stopped",
          this.#state.metadata
        );
      }
    } finally {
      this.#mutex.release();
    }
  }

  async #reportTooManyRequests() {
    const parameters = { reason: "TooManyRequests" };
    const command = backendCommandsFactory.logging.createLogWaring(
      logEntryIds.exportStopped,
      parameters,
      this.#state.metadata
    );
    await this.#broker.publish(command);
  }

  async #logError(error) {
    if (error.skipLogging) {
      return;
    }

    const parameters = errorParametersFactory.create(error);
    for (const metadataItem of this.#contactsIterator.getMetadata()) {
      parameters[metadataItem.key] = metadataItem.value;
    }

    const command = backendCommandsFactory.logging.createLogError(
      logEntryIds.exportError,
      parameters,
      this.#state.metadata
    );
    await this.#broker.publish(command);
  }

  async #fireEvent(event) {
    await this.#broker.fireEvent(event);
  }
}
